import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import * as classes from './index.module.css';

const DESKTOP = {
    rows: 9,
    cols: 16,
};

const TABLET = {
    rows: 12,
    cols: 8,
};

const MOBILE = {
    rows: 16,
    cols: 8,
};

const COLOR_CLASSES = {
    orange: 'bg-orange',
    black: 'bg-black',
    blueLight: 'bg-blueLight',
    beige: 'bg-beige',
    hue: 'bg-hue',
    blur: 'bg-blur',
    white: 'bg-white',
};

const getColorClass = (color) => COLOR_CLASSES[color] || '';

const parseCellId = (id) => {
    const [row, col] = id.split('-').map(Number);
    return { row, col };
};

const getDelayIndex = (row, col) => row + col - 1;

const getRandomDelay = (min, max) => Math.random() * (max - min) + min;

const getImagePosition = ({ config, rows, cols, type }) => {
    if (!config) return null;

    const rawStart =
        type === 'xs'
            ? config.xsStart || config.start
            : config.start;

    const start = rawStart || '1-1';
    const widthCells = (type === 'xs' && config.xsWidthCells) ? config.xsWidthCells : (config.widthCells || 1);
    const aspect = (type === 'xs' && config.xsAspect) ? config.xsAspect : (config.aspect || 'square');
    const aspectRatio = (type === 'xs' && config.xsAspectRatio) ? config.xsAspectRatio : config.aspectRatio;

    const { row, col } = parseCellId(start);

    const aspectMap = {
        video: 16 / 9,
        monitor: 16 / 10,
        screen: 6 / 5,
        square: 1,
        tablet: 4 / 3,
        portrait: 3 / 4,
        mobile: 9 / 16,
    };

    const ratio = aspectRatio || aspectMap[aspect] || 1;
    const width = Math.min(Number(widthCells), cols - col + 1);
    const height = Math.round(width / ratio);

    return {
        gridColumn: `${col} / span ${width}`,
        gridRow: `${row} / span ${Math.min(height, rows - row + 1)}`,
    };
};

const AnimatedGrid = ({
    dImage,
    mImage,
    xsImage,
    dCells = [],
    mCells = [],
    xsCells = [],
    dCols,
    mCols,
    xsCols,
    dRows,
    mRows,
    xsRows,
    once = true,
    delayStep = 0.5,
    randomDelay = true,
    slideAnime = false,
    hasOverlay = false,
    randomDelayRange = [0, 1],
}) => {
    const sectionRef = useRef(null);
    const gridRef = useRef(null);

    const getCellDelay = (cell) => {
        const row = Number(cell.row);
        const col = Number(cell.col);

        if (slideAnime) {
            return Math.floor(Math.random() * 20) + 1;
        }

        return randomDelay
            ? getRandomDelay(randomDelayRange[0], randomDelayRange[1])
            : delayStep * getDelayIndex(row, col);
    };

    const renderGrid = ({ rows, cols, cells, imageConfig, type }) => {
        const activeCells = new Map(cells.map((cell) => [cell.id, cell]));
        const image = getImage(imageConfig?.src);

        const imagePosition = getImagePosition({
            config: imageConfig,
            rows,
            cols,
            type,
        });

        const gridTypeClass = {
            desktop: classes.desktopGrid,
            mobile: xsImage ? classes.tabletGrid : `${classes.mobileGrid} mOnly_grid`,
            xs: classes.xsGrid || `${classes.mobileGrid} smOnly_grid`,
        }[type] || classes.desktopGrid;

        return (
            <div
                className={gridTypeClass}
                style={{
                    '--rows': rows,
                    '--cols': cols,
                    '--dAspect': (dCols && dRows) ? `${dCols}/${dRows}` : '16/9',
                    '--mAspect': (mCols && mRows) ? `${mCols}/${mRows}` : '8/12',
                    '--xsAspect': (xsCols && xsRows)
                        ? `${xsCols}/${xsRows}`
                        : (mCols && mRows)
                            ? `${mCols}/${mRows}`
                            : '8/12',
                }}
            >
                {image && imagePosition && (
                    <div
                        className={`${classes.imageWrapper} ${hasOverlay ? classes.overlayImage : ''}`}
                        style={{
                            gridColumn: imagePosition.gridColumn,
                            gridRow: imagePosition.gridRow,
                        }}
                    >
                        <GatsbyImage
                            image={image}
                            alt={imageConfig?.alt || ''}
                            loading="eager"
                            className={classes.image}
                        />
                    </div>
                )}
                {Array.from({ length: rows * cols }).map((_, index) => {
                    const row = Math.floor(index / cols) + 1;
                    const col = (index % cols) + 1;
                    const id = `${row}-${col}`;
                    const activeCell = activeCells.get(id);
                    const color = activeCell ? getColorClass(activeCell.color) : 'bg-transparent';

                    return (
                        <span
                            key={`${type}-${id}`}
                            className={classes.cellWrapper}
                            data-row={row}
                            data-col={col}
                            data-active={Boolean(activeCell)}
                            style={{
                                gridRow: row,
                                gridColumn: col,
                                '--delay': `${getCellDelay({ row, col })}s`,
                            }}
                        >
                            <span
                                data-anime={slideAnime ? 'slide' : 'ping'}
                                data-delay={getCellDelay({ row, col })}
                                className={`${classes.cell} ${color}`}
                            />
                        </span>
                    );
                })}
            </div>
        );
    };

    return (
        <div ref={sectionRef} className={classes.root}>
            <div ref={gridRef}>
                {renderGrid({
                    rows: dRows ? dRows : DESKTOP.rows,
                    cols: dCols ? dCols : DESKTOP.cols,
                    cells: dCells,
                    imageConfig: dImage,
                    type: 'desktop',
                })}
                {renderGrid({
                    rows: mRows ? mRows : TABLET.rows,
                    cols: mCols ? mCols : TABLET.cols,
                    cells: mCells,
                    imageConfig: mImage,
                    type: 'mobile',
                })}
                {Boolean(xsImage) &&
                    renderGrid({
                        rows: xsRows || mRows || MOBILE.rows,
                        cols: xsCols || mCols || MOBILE.cols,
                        cells: xsCells.length > 0 ? xsCells : mCells,
                        imageConfig: xsImage,
                        type: 'xs',
                    })}
            </div>
        </div>
    );
};

AnimatedGrid.propTypes = {
    dImage: PropTypes.object,
    mImage: PropTypes.object,
    xsImage: PropTypes.object,
    dCells: PropTypes.array,
    mCells: PropTypes.array,
    xsCells: PropTypes.array,
    dCols: PropTypes.number,
    mCols: PropTypes.number,
    xsCols: PropTypes.number,
    dRows: PropTypes.number,
    mRows: PropTypes.number,
    xsRows: PropTypes.number,
    once: PropTypes.bool,
    delayStep: PropTypes.number,
    randomDelay: PropTypes.bool,
    slideAnime: PropTypes.bool,
    hasOverlay: PropTypes.bool,
    randomDelayRange: PropTypes.array,
};

export default AnimatedGrid;